import { useEffect, useState, useCallback } from "react";
import { TopBar } from "../components/TopBar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { api, ROLE_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, KeyRound, Trash2, Edit3, ShieldCheck, ShieldOff, UserCircle2 } from "lucide-react";

const EMPTY = {
  username: "", password: "", name: "", email: "",
  role: "warehouse", company: "WeLove", third_party_id: "",
};

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [thirdParties, setThirdParties] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // user being edited / null
  const [creating, setCreating] = useState(false);
  const [pwUser, setPwUser] = useState(null);     // user whose password is being reset by admin

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, tp, r] = await Promise.all([
        api.get("/users"),
        api.get("/third-parties"),
        api.get("/roles"),
      ]);
      setUsers(u.data);
      setThirdParties(tp.data);
      setRoles(r.data);
    } catch (e) {
      toast.error("Could not load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (u, active) => {
    try {
      await api.patch(`/users/${u.id}`, { active });
      toast.success(`${u.name} ${active ? "activated" : "deactivated"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <TopBar />
      <main className="mx-auto max-w-[1200px] px-4 sm:px-6 py-6">
        <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-zinc-950">
              Users
            </h1>
            <p className="text-sm text-zinc-600 mt-1">
              Manage who can log in, change roles, reset passwords or deactivate accounts.
            </p>
          </div>
          <Button onClick={() => setCreating(true)} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="create-user-button">
            <Plus className="h-4 w-4 mr-2" /> Add user
          </Button>
        </div>

        <div className="bg-white border border-zinc-200 rounded-md shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="text-left py-3 px-4 font-semibold">Name</th>
                <th className="text-left py-3 px-4 font-semibold">Username</th>
                <th className="text-left py-3 px-4 font-semibold">Email</th>
                <th className="text-left py-3 px-4 font-semibold">Role</th>
                <th className="text-left py-3 px-4 font-semibold">Company</th>
                <th className="text-left py-3 px-4 font-semibold">Active</th>
                <th className="text-right py-3 px-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-8 text-center text-zinc-400">Loading…</td></tr>}
              {!loading && users.map((u) => (
                <tr key={u.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`user-row-${u.username}`}>
                  <td className="py-2.5 px-4">
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-md bg-zinc-900 text-white flex items-center justify-center text-[10px] font-bold">
                        {u.name?.split(" ").map(n => n[0]).join("").slice(0,2)}
                      </div>
                      <span className="font-semibold text-zinc-900">{u.name}</span>
                      {u.id === me?.id && <span className="text-[10px] bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded font-mono">You</span>}
                    </div>
                  </td>
                  <td className="py-2.5 px-4 font-mono text-xs text-zinc-700">{u.username}</td>
                  <td className="py-2.5 px-4 text-xs text-zinc-700">{u.email}</td>
                  <td className="py-2.5 px-4 text-xs">
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 font-semibold">
                      {roles.find((r) => r.key === u.role)?.name || ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-xs text-zinc-700">{u.company}</td>
                  <td className="py-2.5 px-4">
                    <Switch
                      checked={u.active !== false}
                      onCheckedChange={(v) => toggleActive(u, v)}
                      disabled={u.id === me?.id}
                      data-testid={`toggle-active-${u.username}`}
                    />
                  </td>
                  <td className="py-2.5 px-4">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => setEditing(u)} className="h-7 border-zinc-200" data-testid={`edit-user-${u.username}`}>
                        <Edit3 className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setPwUser(u)} className="h-7 border-zinc-200" data-testid={`reset-pw-${u.username}`}>
                        <KeyRound className="h-3 w-3 mr-1" /> Reset password
                      </Button>
                      {u.id !== me?.id && (
                        <Button size="sm" variant="outline" onClick={() => remove(u)} className="h-7 border-zinc-200 text-rose-600 hover:bg-rose-50" data-testid={`delete-user-${u.username}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-zinc-500 mt-3 flex items-center gap-1.5">
          <UserCircle2 className="h-3.5 w-3.5" />
          Audit log records the user who made every change — so each entry on a bath record will show your name.
        </p>
      </main>

      <UserDialog
        open={creating || !!editing}
        editing={editing}
        thirdParties={thirdParties}
        roles={roles}
        onClose={() => { setCreating(false); setEditing(null); }}
        onSaved={() => { setCreating(false); setEditing(null); load(); }}
      />

      <ResetPasswordDialog
        targetUser={pwUser}
        onClose={() => setPwUser(null)}
        onSaved={() => setPwUser(null)}
      />
    </div>
  );
}

function UserDialog({ open, editing, thirdParties, roles, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        username: editing.username,
        password: "",
        name: editing.name,
        email: editing.email,
        role: editing.role,
        company: editing.company,
        third_party_id: editing.third_party_id || "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [editing, open]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          name: form.name, email: form.email, role: form.role, company: form.company,
          third_party_id: form.third_party_id || null,
        };
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        const payload = { ...form };
        if (!payload.third_party_id) delete payload.third_party_id;
        await api.post("/users", payload);
        toast.success("User created");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tighter">{editing ? "Edit user" : "Add user"}</DialogTitle>
          <DialogDescription>
            {editing ? "Update name, email, role or company." : "Create a new login. The user can change their password after first login."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
          <Field label="Full name *">
            <Input value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="user-name" />
          </Field>
          <Field label="Email *">
            <Input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} required data-testid="user-email" />
          </Field>
          <Field label="Username *">
            <Input value={form.username} disabled={!!editing} onChange={(e) => upd("username", e.target.value.toLowerCase())} required data-testid="user-username" className="font-mono" />
          </Field>
          {!editing && (
            <Field label="Initial password *">
              <Input type="text" value={form.password} onChange={(e) => upd("password", e.target.value)} required minLength={6} data-testid="user-password" />
            </Field>
          )}
          <Field label="Role">
            <Select value={form.role} onValueChange={(v) => upd("role", v)}>
              <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(roles || []).map((r) => (
                  <SelectItem key={r.key} value={r.key}>
                    {r.name}
                    {!r.is_system && <span className="ml-1.5 text-[9px] uppercase text-fuchsia-600 font-mono">custom</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Company">
            <Select value={form.company} onValueChange={(v) => upd("company", v)}>
              <SelectTrigger data-testid="user-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="WeLove">WeLove</SelectItem>
                <SelectItem value="Giavani">Giavani</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {form.role === "third_party" && (
            <div className="md:col-span-2">
              <Field label="Third party (links record visibility)">
                <Select value={form.third_party_id || "none"} onValueChange={(v) => upd("third_party_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="user-third-party"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {thirdParties.map((tp) => (<SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          )}

          <DialogFooter className="md:col-span-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="user-submit">
              {saving ? "Saving…" : editing ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ targetUser, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setPw(""); }, [targetUser]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    setSaving(true);
    try {
      await api.post(`/users/${targetUser.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${targetUser.name}`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!targetUser} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tighter">Reset password</DialogTitle>
          <DialogDescription>
            Set a new password for <span className="font-semibold">{targetUser?.name}</span>. They'll need to log in with this and can change it themselves afterwards.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 py-2">
          <Field label="New password">
            <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6} data-testid="reset-pw-input" />
          </Field>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="reset-pw-submit">
              {saving ? "Saving…" : "Set password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
